import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <div className="mb-8">
          <Button
            onClick={() => window.history.back()}
            variant="ghost"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tighter mb-4">
            Terms of Service
          </h1>
          <p className="text-slate-600 text-sm">
            Last Updated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-[2rem] border-2 border-slate-100 p-8 md:p-12 shadow-sm space-y-8">
          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              By accessing or using VaultStack Small Business Platform ("the Service"), you agree to be 
              bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, 
              you may not access the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">2. Description of Service</h2>
            <p className="text-slate-700 leading-relaxed">
              VaultStack provides business management software including but not limited to: customer 
              relationship management, quoting, invoicing, booking, inventory management, and task 
              templates. The service is provided on a subscription basis.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">3. Eligibility</h2>
            <p className="text-slate-700 leading-relaxed">
              You must be at least 18 years old and capable of entering into legally binding contracts 
              to use this service. By using the service, you represent and warrant that you meet these 
              requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">4. Account Registration</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              To access the service, you must:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">5. Subscription & Payment</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Subscription Plans:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li><strong>Starter Plan ($19 AUD/month):</strong> Up to 30 customers</li>
              <li><strong>Pro Plan ($49 AUD/month):</strong> Unlimited customers</li>
              <li><strong>Free Trial:</strong> 30 days, no credit card required</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Payments are processed via Stripe, Inc. Subscriptions automatically renew unless cancelled. 
              You may cancel at any time with no refund for partial months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">6. Acceptable Use</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              You agree NOT to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>Use the service for any illegal purpose</li>
              <li>Violate any laws or regulations</li>
              <li>Infringe on others' intellectual property rights</li>
              <li>Transmit harmful code or viruses</li>
              <li>Attempt to gain unauthorized access to the service</li>
              <li>Resell or redistribute the service without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">7. Your Data</h2>
            <p className="text-slate-700 leading-relaxed">
              You retain all rights to data you input into the service. We store your data securely and 
              do not sell it to third parties. You may export or delete your data at any time. See our 
              Privacy Policy for more details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">8. Intellectual Property</h2>
            <p className="text-slate-700 leading-relaxed">
              The service, including its features, design, and content, is owned by VaultStack Pty Ltd 
              and protected by Australian and international intellectual property laws. You receive a 
              limited, non-exclusive, non-transferable license to use the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-slate-700 leading-relaxed">
              The service is provided "as is" and "as available" without warranties of any kind, either 
              express or implied. We do not warrant that the service will be uninterrupted, error-free, 
              or completely secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-700 leading-relaxed">
              To the maximum extent permitted by law, VaultStack Pty Ltd shall not be liable for any 
              indirect, incidental, special, consequential, or punitive damages, or any loss of profits 
              or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, 
              or other intangible losses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">11. Indemnification</h2>
            <p className="text-slate-700 leading-relaxed">
              You agree to indemnify and hold harmless VaultStack Pty Ltd and its officers, directors, 
              employees, and agents from any claims, liabilities, damages, losses, or expenses arising 
              from your use of the service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">12. Termination</h2>
            <p className="text-slate-700 leading-relaxed">
              We may terminate or suspend your access to the service at any time, with or without cause, 
              with or without notice. Upon termination, your right to use the service will immediately 
              cease. You may terminate your account at any time by contacting support.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">13. Governing Law</h2>
            <p className="text-slate-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of 
              Queensland, Australia, without regard to its conflict of law provisions. Any disputes shall 
              be subject to the exclusive jurisdiction of the courts of Queensland.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">14. Changes to Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify these terms at any time. We will provide notice of significant 
              changes by posting the new terms on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">15. Contact Information</h2>
            <p className="text-slate-700 leading-relaxed">
              For any questions about these Terms, please contact us at:{' '}
              <a href="mailto:legal@vaultstack.com.au" className="text-purple-600 hover:underline font-bold">
                legal@vaultstack.com.au
              </a>
            </p>
          </section>

          <section className="border-t-2 border-slate-100 pt-8">
            <h2 className="text-xl font-black text-slate-900 uppercase mb-4">Australian Consumer Law</h2>
            <p className="text-slate-700 leading-relaxed">
              Our services come with guarantees that cannot be excluded under the Australian Consumer Law. 
              For major failures, you are entitled to a refund or replacement. For other failures, you are 
              entitled to repair or compensation.
            </p>
          </section>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-xs text-slate-500 font-bold uppercase tracking-widest">
          VaultStack Pty Ltd ABN 95 691 143 665
        </div>
      </div>
    </div>
  );
}
